/**
 * Runs `task` once the browser is idle, so non-essential work stays off the
 * critical path of the first render.
 *
 * `timeout` is a ceiling, not a delay: if the browser never goes idle — which
 * is exactly what happens on a busy page — the callback is run anyway once it
 * elapses. Without it, an analytics SDK on a permanently busy tab could be
 * deferred indefinitely.
 *
 * Falls back to a macrotask where `requestIdleCallback` is missing (older
 * Safari). That still yields to paint, which is the point.
 *
 * @returns a function that cancels the pending task if it has not run yet.
 */
export const whenIdle = (
  task: () => void,
  { timeout = 3000 }: { timeout?: number } = {}
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(task, { timeout });
    return () => window.cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(task, 1);
  return () => window.clearTimeout(handle);
};
